/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { getFormValues } from 'redux-form';
import Collapsible from 'react-collapsible';
import _ from 'lodash';

import styles from './styles.module.scss';
import ActionButton from '../../../../components/ActionButton';
import IconButton from '../../../../components/IconButton';
import UsageForm from '../../../../forms/UsageForm';
import actions from '../../../../redux/actions';
import { getUsages, getUsage, getIsLoadingUsages } from '../../../../redux/reducers/usages';
import liferay from '../../../../services/api';
import protonesUtils from '../../../../utils/protones';

import type {
  SaveUsageParams,
  Fetchable,
  Usage,
  SimplifiedUsage,
  Translate,
  ReduxState,
} from '../../../../types';

const mapStateToProps = (state: ReduxState, ownProps) => {
  return {
    usageFormValues: getFormValues('worklistUsage')(state),
    usages: getUsages(state),
    usage: getUsage(state),
    isLoading: getIsLoadingUsages(state),
  };
};

type Props = {
  ...Usage,
  usageFormValues: Object,
  usages: Fetchable<SimplifiedUsage[]>,
  usage: Fetchable<Usage>,
  isReadOnlyUsage?: boolean,
  onSave?: (usageParams: SaveUsageParams) => mixed,
  onSaveAsPreset?: (usageParams: SaveUsageParams) => mixed,
  onDeletePreset?: (usageId: number) => mixed,
  onCreateProduction?: () => mixed,
  fetchUsageList: typeof actions.fetchUsageList,
  fetchUsage: typeof actions.fetchUsage,
  isLoading: boolean,
  t: Translate,
};

type State = {
  selectedUsageId: ?number,
  fetchedUsage: ?Usage,
  isDetailsExpanded: boolean,
};

class UsageInfo extends React.Component<Props, State> {
  state = {
    isDetailsExpanded: false,
    selectedUsageId: null,
    fetchedUsage: null,
  };

  componentDidMount() {
    this.props.fetchUsageList();
  }

  componentDidUpdate(prevProps: Props) {
    const fetchedUsage = _.get(this.props.usage, 'data');
    if (!_.isEqual(prevProps.usage, this.props.usage) && fetchedUsage) {
      this.setState({ fetchedUsage });
    }
  }

  _handleDetailsExpandClick = () => {
    this.setState({ isDetailsExpanded: !this.state.isDetailsExpanded });
  };

  _handleSaveUsageAsPreset = () => {
    const { onSaveAsPreset, usageFormValues } = this.props;
    if (typeof onSaveAsPreset === 'function') {
      onSaveAsPreset(_.omit(usageFormValues, ['id', 'name']));
    }
  };

  _handleCreateUsagePreset = () => {
    const { onSaveAsPreset } = this.props;
    if (typeof onSaveAsPreset === 'function') {
      onSaveAsPreset({ id: 0, name: '' });
    }
  };

  _handleEditUsagePreset = async (usagePreset: SaveUsageParams) => {
    const { onSaveAsPreset } = this.props;
    if (typeof onSaveAsPreset === 'function') {
      const usage = await liferay.usages.get(_.get(usagePreset, 'id'));
      const usageParams = protonesUtils.getUsageFormValuesFromUsage(usage);
      onSaveAsPreset(usageParams);
    }
  };

  _handleDeleteUsagePreset = (usageId: number) => {
    const { onDeletePreset } = this.props;
    if (typeof onDeletePreset === 'function') {
      onDeletePreset(usageId);
    }
  };

  _handleSaveUsage = () => {
    const { onSave, usageFormValues } = this.props;
    if (typeof onSave === 'function') {
      onSave(usageFormValues);
    }
  };

  _handleSelectUsagePreset = (selectedUsageId?: number) => {
    if (selectedUsageId != null) {
      this.props.fetchUsage(selectedUsageId);
    }
    this.setState({ selectedUsageId });
  };

  _renderUsageOptions = () => {
    const { isLoading, t } = this.props;
    // const { selectedUsageId } = this.state; // TODO: use this to remind last applied usage preset
    const usages = _.get(this.props.usages, 'data', []);
    return (
      <React.Fragment>
        <span style={{ flex: 1 }}></span>
        <span style={{ flex: 1, marginLeft: '-2em' }}>
          <ActionButton
            iconName={'usage'}
            loading={isLoading}
            label={t('SELECT_A_USAGE_PRESET')}
            tooltip={t('SELECT_A_USAGE_PRESET')}
            menuExpandDirection={'right'}
            items={[
              {
                iconName: 'add',
                label: t('CREATE_NEW_USAGE_PRESET'),
                onClick: this._handleCreateUsagePreset,
                tooltip: t('CREATE_NEW_USAGE_PRESET'),
              },
              {
                iconName: 'add',
                label: t('COPY_AS_USAGE_PRESET'),
                onClick: this._handleSaveUsageAsPreset,
                tooltip: t('COPY_AS_USAGE_PRESET'),
              },
            ].concat(
              usages.map(usage => ({
                iconName: 'usage',
                label: usage.name,
                actions: (
                  <React.Fragment>
                    <IconButton
                      icon={'edit'}
                      tooltip={`${t('EDIT')} ${usage.name}`}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        this._handleEditUsagePreset(usage);
                      }}
                    />
                    <IconButton
                      icon={'delete'}
                      tooltip={`${t('DELETE')} ${usage.name}`}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        this._handleDeleteUsagePreset(usage.id);
                      }}
                    />
                  </React.Fragment>
                ),
                onClick: () => this._handleSelectUsagePreset(usage.id),
                tooltip: `${t('SELECT_THIS_USAGE_PRESET')}`,
              }))
            )}
          />
        </span>
      </React.Fragment>
    );
  };

  render() {
    const { fetchedUsage } = this.state;
    const { onCreateProduction, isReadOnlyUsage = false, t } = this.props;
    let usage = protonesUtils.getUsageFormValuesFromUsage(this.props);
    if (fetchedUsage) {
      usage = {
        ...protonesUtils.getUsageFormValuesFromUsage(fetchedUsage),
        id: usage.id,
      };
    }
    return (
      <div className={styles.container}>
        <div className={styles.usageRow}>
          <h3>{t('USAGE')}</h3>
          <div>
            <ActionButton
              iconName={this.state.isDetailsExpanded ? 'expand_less' : 'expand_more'}
              onClick={this._handleDetailsExpandClick}
            />
          </div>
        </div>
        <div className={styles.usageRow}>
          {this.state.isDetailsExpanded && this._renderUsageOptions()}
        </div>
        <Collapsible
          open={this.state.isDetailsExpanded}
          transitionTime={200}
          easing={'ease-in-out'}
        >
          <UsageForm
            form={'worklistUsage'}
            isWorklistUsage={true}
            initialValues={usage}
            readOnly={isReadOnlyUsage}
            isLoading={false}
            actionButtonsVisible={true}
            save={this._handleSaveUsage}
            saveAsPreset={this._handleSaveUsageAsPreset}
            onCreateProduction={onCreateProduction}
          />
        </Collapsible>
      </div>
    );
  }
}

export default withTranslation()(connect(mapStateToProps, actions)(UsageInfo));
